import type { AppRouter } from '@app/api'
import { type CalendarSpelling, DEFAULT_CALENDAR_SPELLING } from '@app/shared'
import {
  appKey,
  DEFAULT_PRODUCT,
  type Product,
  productForOrgKind,
} from '@app/shared/brand'
import { useQuery } from '@tanstack/react-query'
import type { inferRouterOutputs } from '@trpc/server'
import { createContext, type ReactNode, useContext, useState } from 'react'
import { currentProduct } from './public-router'
import { useTRPC } from './trpc'

/** One org as the list endpoint returns it — full detail (identity + geo). */
export type OrgListItem = inferRouterOutputs<AppRouter>['org']['list'][number]

/** A place this person can work — an org they hold authority at, or their own. */
export type SpaceChoice = inferRouterOutputs<AppRouter>['space']['mine'][number]

type SpaceContextValue = {
  /**
   * Everywhere this person can work **in this product** — the others are not
   * hidden, they are somewhere else, and the app switcher is how you get there.
   */
  spaces: SpaceChoice[]
  /** Everywhere this person can work, in every product — what the app
   *  switcher offers is read from it. */
  everywhere: SpaceChoice[]
  /** Their orgs, in full — geo, letterhead, everything a shul screen needs. */
  orgs: OrgListItem[]
  /** The active tenant SPACE id — the key every scoped call carries. */
  spaceId: string | null
  /** What kind of place this is. An org's work is not a person's. */
  kind: SpaceChoice['kind'] | null
  /**
   * Whether this person changes the active space's documents: their own space,
   * or an org where they hold `documents.edit`. A viewer reads the shelf and is
   * offered nothing that would change it.
   */
  editsDocuments: boolean
  setSpaceId: (spaceId: string) => void
  /**
   * Still asking where this person can work.
   *
   * Published because "no spaces" and "not yet told" look identical from the
   * outside and mean opposite things: one is a person with nowhere to work, the
   * other is a request in flight. A screen that cannot tell them apart shows the
   * empty state to everybody for a frame on every load.
   */
  loading: boolean
}

const SpaceContext = createContext<SpaceContextValue | null>(null)

/**
 * Which kind of space a product's screens are about.
 *
 * ShulBase's screens resolve an org, Studio's a person's own — and Studio also
 * an org whose documents this person keeps (`inProduct`). That is what makes
 * "entering Studio puts you in your own space" a rule rather than a step
 * somebody remembers — it is where Studio opens until somebody picks another.
 */
const KIND_FOR_PRODUCT: Record<Product, SpaceChoice['kind']> = {
  [DEFAULT_PRODUCT]: 'org',
  terumah: 'org',
  studio: 'personal',
}

/**
 * The same question, for a screen with no active space to ask.
 *
 * Signing in before belonging anywhere is one — the space is null there, and
 * the only thing known is which product's front door it is. Reading the map is
 * what keeps that screen from spelling out the product name a second time.
 */
export const kindForProduct = (product: Product): SpaceChoice['kind'] =>
  KIND_FOR_PRODUCT[product]

/**
 * Where the active space id is remembered across reloads — one key per
 * PRODUCT.
 *
 * Keyed that way so a trip to another product does not cost somebody the org
 * they were working in: each product chooses over a set of its own, and a
 * single key would have each overwriting the other's answer on every switch.
 * The person is part of the key; a key without one is read as a fallback only.
 */
const storageKey = (product: Product, person?: string) =>
  person
    ? appKey('activeSpaceId', product, person)
    : appKey('activeSpaceId', product)

/** A stored choice, or null where storage is unavailable. */
function readStored(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function writeStored(key: string, value: string) {
  try {
    localStorage.setItem(key, value)
  } catch {
    /* The choice still holds for this visit; it is only not remembered. */
  }
}

/**
 * Whether a space is one a product works in: its kind of space, and for an
 * org, the product the org's own kind belongs to — a shul's staff meet it on
 * ShulBase, a charity's on Terumah.
 *
 * Studio also works in every org whose documents this person reads or keeps,
 * because an org's documents are made and read where every document is.
 */
export const inProduct = (space: SpaceChoice, product: Product): boolean =>
  (space.kind === KIND_FOR_PRODUCT[product] &&
    (space.kind !== 'org' || productForOrgKind(space.orgKind) === product)) ||
  (KIND_FOR_PRODUCT[product] === 'personal' && readsDocuments(space))

/** Whether this person reads the org's documents there; edit carries view. */
const readsDocuments = (space: SpaceChoice): boolean =>
  space.kind === 'org' && space.permissions.includes('documents.view')

/** Whether this person changes the documents a space holds. */
export const editsDocumentsIn = (space: SpaceChoice): boolean =>
  space.kind === 'personal' ||
  (space.kind === 'org' && space.permissions.includes('documents.edit'))

/** Which products a person works in, from the spaces they hold. */
export const productsOf = (spaces: SpaceChoice[]): Set<Product> =>
  new Set(spaces.map(productOf))

/**
 * The products a person staffs an org in. A person's own space is not one:
 * every account holds one, so it says nothing about where they work.
 */
export const orgProductsOf = (spaces: SpaceChoice[]): Set<Product> =>
  productsOf(spaces.filter((s) => s.kind === 'org'))

/** The product a space is worked in, read from the same map the provider uses. */
function productOf(space: SpaceChoice): Product {
  if (space.kind === 'org') return productForOrgKind(space.orgKind)
  const own = (Object.keys(KIND_FOR_PRODUCT) as Product[]).find(
    (p) => KIND_FOR_PRODUCT[p] === space.kind,
  )
  /* The map is keyed by every product and names one for each kind of space. */
  if (!own) throw new Error(`No product works in a ${space.kind} space.`)
  return own
}

/**
 * Where you are working.
 *
 * App-level rather than per-page: a gabbai belongs to an org, they do not
 * compare orgs. Every screen was asking the same question in its own toolbar
 * and answering it differently on each; this makes it one answer, chosen once,
 * in the shell.
 *
 * A person's OWN space is one of the choices, and that is what makes personal
 * scope reachable at all: owning a space is authority over it, so the same
 * scoped calls work there with no grant involved.
 *
 * **The product decides which of them are on offer.** Studio works in a
 * person's own space and in every org whose documents they read, and ShulBase
 * and Terumah each in the orgs whose kind belongs to them, so each shows only
 * its own and resolves the active one against that — which is also what stops
 * a personal space last chosen in Studio from meeting the members table on the
 * way back, and a charity from meeting a shul's luach.
 *
 * Two queries, because they answer different questions: `space.mine` is where
 * you can work (the switcher), `org.mine` is what those orgs ARE (the geo a
 * luach is computed from). Folding them into one would make the switcher carry
 * a shul's coordinates in order to draw a name.
 */
export function SpaceProvider({ children }: { children: ReactNode }) {
  const trpc = useTRPC()
  const mine = useQuery(trpc.space.mine.queryOptions())
  const orgs = useQuery(trpc.org.mine.queryOptions())
  /*
   * The platform surface belongs to no product and works across every space,
   * so it filters nothing — it reads a space by id rather than about the one
   * you are "in".
   */
  const product = currentProduct()
  const all = mine.data ?? []
  /* The person's own space names them, so two people sharing a browser each
     keep their own answer. */
  const person = all.find((s) => s.kind === 'personal')?.spaceId ?? null
  const key = product && person ? storageKey(product, person) : null
  const [picked, setPicked] = useState<string | null>(null)
  const chosen =
    picked ??
    (key ? readStored(key) : null) ??
    (product
      ? (readStored(storageKey(product)) ??
        readStored(appKey('activeSpaceId', KIND_FOR_PRODUCT[product])))
      : null)

  const setSpaceId = (spaceId: string) => {
    if (key) writeStored(key, spaceId)
    setPicked(spaceId)
  }

  const spaces = product ? all.filter((s) => inProduct(s, product)) : all
  // Honor the stored choice only while it is still one of theirs; otherwise
  // fall back to the product's own kind of space, which is the work they came
  // here to do.
  const current =
    (chosen ? spaces.find((s) => s.spaceId === chosen) : undefined) ??
    (product
      ? spaces.find((s) => s.kind === KIND_FOR_PRODUCT[product])
      : undefined) ??
    spaces[0]

  return (
    <SpaceContext.Provider
      value={{
        spaces,
        everywhere: all,
        orgs: orgs.data ?? [],
        spaceId: current?.spaceId ?? null,
        kind: current?.kind ?? null,
        editsDocuments: current ? editsDocumentsIn(current) : false,
        setSpaceId,
        loading: mine.isPending,
      }}
    >
      {children}
    </SpaceContext.Provider>
  )
}

export function useSpace(): SpaceContextValue {
  const ctx = useContext(SpaceContext)
  if (!ctx) throw new Error('useSpace must be used inside a SpaceProvider')
  return ctx
}

/**
 * The person's own space, whichever space is active.
 *
 * For what is somebody's own work of which there is one — their song library
 * — and so stays theirs while they keep an org's documents in Studio.
 */
export function useOwnSpaceId(): string | null {
  const { everywhere } = useSpace()
  return everywhere.find((s) => s.kind === 'personal')?.spaceId ?? null
}

/**
 * The space being worked in, or null where there is none to be in.
 *
 * A public page renders for a visitor with no session, outside every
 * SpaceProvider, so a hook shared between it and the office asks here rather
 * than through `useSpace`, which throws.
 */
export function useSpaceIfAny(): SpaceContextValue | null {
  return useContext(SpaceContext)
}

/**
 * The org you are working in, for the screens that are an org's work.
 *
 * `spaceId` is null in a personal space — not because the space has no id, but
 * because these screens have no subject there. A members table pointed at a
 * personal tenant would resolve nobody and read as an empty shul rather than as
 * a screen that does not belong.
 */
export function useOrg(): {
  orgs: OrgListItem[]
  spaceId: string | null
  setSpaceId: (spaceId: string) => void
} {
  const { orgs, spaceId, kind, setSpaceId } = useSpace()
  return { orgs, spaceId: kind === 'org' ? spaceId : null, setSpaceId }
}

/**
 * Whether the org being worked in is a demo — one that may reach nobody.
 *
 * Read off the orgs already loaded for the switcher rather than fetched, so a
 * screen asking the question costs no request. False while they are still
 * loading, which is the safe way round for the callers there are: a screen
 * treating a demo as ordinary for one frame shows a prefilled field, where the
 * reverse would blank a real user's own address as they typed.
 */
export function useIsDemoOrg(): boolean {
  const { orgs, spaceId } = useOrg()
  return orgs.find((o) => o.spaceId === spaceId)?.isDemo ?? false
}

/**
 * What kind of org a space belongs to — shul, school, chesed, group — or null
 * where it is no org's, or not yet known.
 *
 * Read off what is already loaded, like `useIsDemoOrg`, and asked of the space
 * named rather than the one chosen, since the platform surface works in a
 * space by id. Safe outside a SpaceProvider, where nothing is known.
 */
export function useOrgKind(spaceId: string): string | null {
  const space = useSpaceIfAny()
  if (!space) return null
  const org = space.orgs.find((o) => o.spaceId === spaceId)
  if (org) return org.kind
  const choice = space.everywhere.find((s) => s.spaceId === spaceId)
  return choice?.kind === 'org' ? choice.orgKind : null
}

/**
 * How the org being worked in spells its Hebrew words in English — what every
 * word the app writes about Shabbos, and every Hebrew month it names, follows
 * (`spelled`, `hebrewMonthName` in `@app/shared`).
 *
 * Read off the orgs already loaded, like `useIsDemoOrg`. Outside an org — a
 * person's own space — there is no shul to ask, and the default answers.
 */
export function useCalendarSpelling(): CalendarSpelling {
  const { orgs, spaceId } = useOrg()
  return (
    orgs.find((o) => o.spaceId === spaceId)?.calendarSpelling ??
    DEFAULT_CALENDAR_SPELLING
  )
}
